import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, TrendingUp, LogOut } from "lucide-react";

type OptionCardProps = {
  icon: React.ReactNode;
  title: string;
  onPress: () => void;
};

const OptionCard: React.FC<OptionCardProps> = ({ icon, title, onPress }) => {
  return (
    <button
      type="button"
      onClick={onPress}
      className="flex-1 bg-white border border-gray-200 rounded-xl p-3 shadow-[0_2px_12px_rgba(0,0,0,0.08)]"
    >
      <div className="flex flex-row items-center gap-3">
        <div className="w-9 h-9 rounded-full border border-primary/70 bg-gray-50 p-[2px] flex items-center justify-center shrink-0">
          <div className="w-full h-full rounded-full bg-white border border-gray-300/60 flex items-center justify-center p-1.5">
            {icon}
          </div>
        </div>
        <div className="text-primary font-medium text-left text-sm leading-none">{title}</div>
      </div>
    </button>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => {
    const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
    const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
    if (!envApiUrl) return fallback;
    try {
      const u = new URL(envApiUrl);
      if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
      return envApiUrl;
    } catch {
      return envApiUrl || fallback;
    }
  }, []);
  const token = localStorage.getItem("token") || "";
  const firstName = localStorage.getItem("userFirstName") || "";
  const lastName = localStorage.getItem("userLastName") || "";
  const userName = `${firstName} ${lastName}`.trim();
  const teamName = localStorage.getItem("userTeamName") || "";
  const [loadingTopUp, setLoadingTopUp] = useState(false);
  const [topUpError, setTopUpError] = useState("");
  const [topUpHistory, setTopUpHistory] = useState<
    Array<{ id: string; outAt: string; refNo: string | null; batchNo: string | null; totalQty: number }>
  >([]);

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(value));

  useEffect(() => {
    const loadTopUpHistory = async () => {
      if (!token) return;
      setLoadingTopUp(true);
      setTopUpError("");
      try {
        const res = await fetch(`${apiUrl}/supervisor/stock-topup-history`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: any = await res.json().catch(() => ({}));
        if (!res.ok) {
          setTopUpError(json?.message || `Failed to load stock top-up history (${res.status}).`);
          return;
        }
        setTopUpHistory(Array.isArray(json?.history) ? json.history : []);
      } catch {
        setTopUpError("Unable to connect to server. Please try again.");
      } finally {
        setLoadingTopUp(false);
      }
    };

    void loadTopUpHistory();
  }, [apiUrl, token]);

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userFirstName");
    localStorage.removeItem("userLastName");
    localStorage.removeItem("userTeamName");
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      <div className="h-[102px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              {userName ? (
                <div className="min-w-0">
                  <div className="text-xs font-normal opacity-90 truncate">Welcome</div>
                  <div className="text-base font-bold mt-1 truncate">{userName}</div>
                  {teamName ? (
                    <div className="text-xs font-medium opacity-90 truncate mt-0.5">{teamName}</div>
                  ) : null}
                </div>
              ) : (
                <div className="text-2xl font-semibold">Home</div>
              )}
            </div>

            {teamName ? (
              <button
                type="button"
                onClick={handleLogout}
                className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
                aria-label="Logout"
              >
                <LogOut size={20} className="text-white" />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="px-4 pt-2 pb-2 bg-white">
        <div className="grid grid-cols-2 gap-3">
          <OptionCard
            title="Stock"
            onPress={() => navigate("/app/stock")}
            icon={<Package size={18} className="text-primary" />}
          />
          <OptionCard
            title="Sales"
            onPress={() => navigate("/app/sales")}
            icon={<TrendingUp size={18} className="text-primary" />}
          />
        </div>
      </div>

      <div className="px-4 py-4 bg-white border-t border-gray-200">
        <div className="text-sm font-semibold text-slate-900">Stock Top-Up History</div>
        <div className="mt-1 text-xs text-slate-500">Latest stock out date for your team</div>

        {loadingTopUp ? <div className="mt-3 text-sm text-slate-500">Loading...</div> : null}
        {topUpError ? <div className="mt-3 text-sm text-red-600">{topUpError}</div> : null}

        {!loadingTopUp && !topUpError && topUpHistory.length === 0 ? (
          <div className="mt-3 text-sm text-slate-500">No stock top-up records found.</div>
        ) : null}

        {!loadingTopUp && !topUpError && topUpHistory.length > 0 ? (
          <div className="mt-3 space-y-2">
            <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
              <div className="text-xs text-slate-600">Latest Top-Up</div>
              <div className="text-sm font-semibold text-slate-900">{formatDateTime(topUpHistory[0].outAt)}</div>
              <div className="text-xs text-slate-600 mt-0.5">
                Qty: {topUpHistory[0].totalQty}
                {topUpHistory[0].batchNo ? ` • ${topUpHistory[0].batchNo}` : ""}
              </div>
            </div>

            <div>
              {topUpHistory.slice(1).map((item) => (
                <div key={item.id} className="px-1 py-2 border-b border-gray-100 last:border-b-0">
                  <div className="text-xs font-medium text-slate-900">{formatDateTime(item.outAt)}</div>
                  <div className="text-[11px] text-slate-600">
                    Qty: {item.totalQty}
                    {item.batchNo ? ` • ${item.batchNo}` : ""}
                    {item.refNo ? ` • Ref: ${item.refNo}` : ""}
                  </div>
              </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      <div className="h-6" />
    </div>
  );
}

